import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase/client';
import { toast } from '@/lib/utils/toast';

type Suggestion = {
  title: string;
  description?: string;
  criteria?: string;
  city?: string | null;
  venueIds: string[];
};

export function ShortlistSuggestions({ city }: { city?: string }) {
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [creating, setCreating] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data: sessionRes } = await supabase.auth.getSession();
        const token = sessionRes?.session?.access_token;
        if (!token) return;

        const res = await fetch('/.netlify/functions/shortlist-suggest', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({ city, max: 5 })
        });
        const json = await res.json();
        setSuggestions(json?.suggestions || []);
      } catch {
        // noop
      } finally {
        setLoading(false);
      }
    })();
  }, [city]);

  const createShortlist = async (s: Suggestion) => {
    try {
      setCreating(s.title);
      const { data: sessionRes } = await supabase.auth.getSession();
      const token = sessionRes?.session?.access_token;
      if (!token) return;
      const res = await fetch('/.netlify/functions/shortlist-create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ title: s.title, description: s.description, city: s.city || city, venueIds: s.venueIds })
      });
      if (!res.ok) throw new Error('Failed to create shortlist');
      const payload = await res.json();
      await navigator.clipboard.writeText(`${window.location.origin}${payload.link}`);
      toast.success('Shortlist created! Link copied');
    } catch {
      toast.error('Could not create shortlist');
    } finally {
      setCreating(null);
    }
  };

  if (loading) {
    return (
      <div className="p-4 border rounded bg-earth-800 border-earth-700 text-sand-300">Generating shortlist ideas…</div>
    );
  }

  if (suggestions.length === 0) {
    return null;
  }

  return (
    <div className="space-y-3">
      {suggestions.map((s) => (
        <div key={s.title} className="p-4 border rounded bg-earth-800 border-earth-700">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sand-100 font-medium">{s.title}</div>
              {s.description && <div className="text-sand-400 text-sm mt-1">{s.description}</div>}
              {s.criteria && <div className="text-ash text-xs mt-1">{s.criteria}</div>}
            </div>
            <button
              className="px-3 py-1.5 bg-olive text-bone rounded disabled:opacity-60"
              onClick={() => createShortlist(s)}
              disabled={!!creating}
            >
              {creating === s.title ? 'Creating…' : 'Create'}
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}







































































